/**
 * sl-t828.2.1: Hook for workspace creation quota.
 *
 * Queries the user-level workspaceCreationQuota to check
 * whether the user can create new workspaces based on their plan.
 *
 * For unauthenticated users (local workspace mode), the GraphQL query is
 * skipped. Instead, the local workspace count is derived from the client-side
 * workspace list. Local users are limited to 1 workspace and see an upgrade
 * CTA when that limit is reached.
 */
import { AuthService, GraphQLService } from '@affine/core/modules/cloud';
import { WorkspacesService } from '@affine/core/modules/workspace';
import { workspaceCreationQuotaQuery } from '@affine/graphql';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo, useState } from 'react';

export interface WorkspaceCreationQuota {
  canCreate: boolean;
  ownedCount: number;
  limit: number;
  plan: string;
  upgradeAvailable: boolean;
  loading: boolean;
}

const LOCAL_WORKSPACE_LIMIT = 1;
const CLOUD_WORKSPACE_QUOTA_FALLBACK: WorkspaceCreationQuota = {
  canCreate: true,
  ownedCount: 0,
  limit: -1,
  plan: 'Free',
  upgradeAvailable: false,
  loading: false,
};

export function useWorkspaceCreationQuota(): WorkspaceCreationQuota {
  const session = useLiveData(useService(AuthService).session.session$);
  const isAuthenticated = session.status === 'authenticated';
  const graphqlService = useService(GraphQLService);

  const workspacesService = useService(WorkspacesService);
  const workspaces = useLiveData(workspacesService.list.workspaces$);
  const localCount = useMemo(
    () => workspaces.filter(w => w.flavour === 'local').length,
    [workspaces]
  );
  const [cloudQuota, setCloudQuota] = useState<WorkspaceCreationQuota>(
    CLOUD_WORKSPACE_QUOTA_FALLBACK
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setCloudQuota(CLOUD_WORKSPACE_QUOTA_FALLBACK);
      return;
    }

    let disposed = false;
    setCloudQuota(previous => ({ ...previous, loading: true }));
    graphqlService
      .gql({ query: workspaceCreationQuotaQuery })
      .then(data => {
        if (disposed) return;
        const quota = data.workspaceCreationQuota;
        setCloudQuota({
          canCreate: quota.canCreate,
          ownedCount: quota.ownedCount,
          limit: quota.limit,
          plan: quota.plan,
          upgradeAvailable: quota.upgradeAvailable,
          loading: false,
        });
      })
      .catch(error => {
        if (disposed) return;
        console.warn(
          '[useWorkspaceCreationQuota] Failed to fetch quota:',
          error
        );
        setCloudQuota(CLOUD_WORKSPACE_QUOTA_FALLBACK);
      });

    return () => {
      disposed = true;
    };
  }, [graphqlService, isAuthenticated]);

  if (!isAuthenticated) {
    return {
      canCreate: localCount < LOCAL_WORKSPACE_LIMIT,
      ownedCount: localCount,
      limit: LOCAL_WORKSPACE_LIMIT,
      plan: 'Local',
      upgradeAvailable: true,
      loading: false,
    };
  }

  return cloudQuota;
}
